import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import SuppressedTab from '@/components/SuppressedTab';
import { LondonClock, ukInputToISO, fmtUkLong, relativeTo } from '@/components/SchedulePanel';
import {
  Search, Trash2, FileText, X, ChevronLeft, ChevronRight, Filter, ExternalLink, RotateCcw,
  CalendarClock, AlertTriangle, Lock, Loader2, Columns3, ArrowUp, ArrowDown,
  CheckCircle, XCircle, BookX, Rocket, Send, Edit3, Save, PenLine, Workflow, Check, RefreshCw, Clock,
} from 'lucide-react';

const STATUS_STYLES = {
  draft:           'bg-stone-100 text-stone-600 border border-stone-300',
  review:          'bg-amber-100 text-amber-700 border border-amber-300',
  scheduled:       'bg-[#FFE4C4] text-[#1F1A17] border border-[#E7D2B8]',
  published:       'bg-emerald-100 text-emerald-700 border border-emerald-300',
  unpublished:     'bg-zinc-100 text-zinc-600 border border-zinc-300',
  refused:         'bg-red-100 text-red-700 border border-red-300',
  failed:          'bg-red-200 text-red-800 border border-red-400',
  needs_attention: 'bg-orange-100 text-orange-700 border border-orange-300',
  approved:        'bg-emerald-100 text-emerald-700 border border-emerald-300',
};

const STATUS_LABELS = {
  draft: 'Draft', review: 'In Review', scheduled: 'Scheduled', published: 'Published',
  unpublished: 'Unpublished', refused: 'Refused',
  failed: 'Failed', needs_attention: 'Needs Attention', approved: 'Published',
};

const storyTypeLabels = {
  press_release:      'Press Release',
  executive_move:     'Executive Move',
  funding:            'Funding',
  partnership:        'Partnership',
  product_launch:     'Product Launch',
  regulation:         'Regulation',
  tender_procurement: 'Tender & Procurement',
  legal:              'Legal',
  event:              'Event',
  market_update:      'Market Update',
  other:              'Other',
};

const SORT_OPTIONS = [
  { value: 'updated',  label: 'Last updated' },
  { value: 'public',   label: 'Public date' },
  { value: 'schedule', label: 'Scheduled time' },
  { value: 'created',  label: 'Newest first' },
  { value: 'oldest',   label: 'Oldest first' },
  { value: 'headline', label: 'Headline A–Z' },
];

const ASC_SORTS = ['oldest', 'headline', 'schedule'];

const PARENT_TABS = [
  { id: 'all',       label: 'All News' },
  { id: 'casted',    label: 'Casted News' },
  { id: 'eventflow', label: 'EventFlow News' },
];

// Suppressed is EventFlow-only — casted stories have no source feed item.
const SUBTABS = {
  all:       ['all', 'draft', 'review', 'scheduled', 'published', 'unpublished', 'attention', 'deleted', 'suppressed'],
  casted:    ['all', 'draft', 'review', 'scheduled', 'published', 'unpublished', 'refused', 'deleted'],
  eventflow: ['all', 'scheduled', 'published', 'unpublished', 'attention', 'deleted', 'suppressed'],
};
const CASTER_SUBTABS = ['all', 'draft', 'review', 'scheduled', 'published', 'unpublished', 'refused'];

const SUBTAB_LABELS = {
  all: 'All', draft: 'Drafts', review: 'In Review', scheduled: 'Scheduled',
  published: 'Published', unpublished: 'Unpublished', refused: 'Refused',
  attention: 'Attention', deleted: 'Deleted', suppressed: 'Suppressed',
};

const DEFAULT_SORT = {
  review: 'oldest', draft: 'oldest',
  published: 'public', unpublished: 'public',
  scheduled: 'schedule',
  refused: 'updated', attention: 'updated', all: 'updated',
  deleted: 'updated', suppressed: 'updated',
};

const COLUMNS = [
  { id: 'publication', label: 'Publication' },
  { id: 'status',      label: 'Status' },
  { id: 'assigned',    label: 'Assigned' },
  { id: 'client',      label: 'Client / Project' },
  { id: 'subject',     label: 'Subject' },
  { id: 'type',        label: 'Type' },
  { id: 'tags',        label: 'Tags' },
  { id: 'scheduled',   label: 'Scheduled for', sorts: ['schedule'] },
  { id: 'public_date', label: 'Public date', sorts: ['public'] },
  { id: 'created',     label: 'Created',     sorts: ['created', 'oldest'] },
  { id: 'updated',     label: 'Updated',     sorts: ['updated'] },
];

const VERB = {
  approve: 'published', publish: 'published', unpublish: 'unpublished',
  submit: 'submitted', 'save-draft': 'saved to draft', delete: 'moved to Deleted',
  restore: 'restored', permanent: 'permanently deleted',
  'cancel-schedule': 'taken off the schedule', 'retry-publish': 'queued for retry',
};

const SCHEDULABLE = ['review', 'approved', 'unpublished', 'scheduled'];

const emptyFilters = {
  client_id:           'all',
  project_name:        '',
  publication_id:      'all',
  story_type:          'all',
  tag:                 'all',
  caster_id:           'all',
  geography:           '',
  date_from:           '',
  date_to:             '',
  date_published_from: '',
  date_published_to:   '',
};

const FILTER_DEFAULT = (key) => emptyFilters[key];

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 350;

const LS_COLS    = 'tempust.newsroom.hiddenCols';
const LS_DENSITY = 'tempust.newsroom.density';

const readStored = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const writeStored = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable — not worth surfacing */
  }
};

const fmtUK = (value, withTime = true) => {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    day: 'numeric', month: 'short', year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
};

// Builds the correct PubLauncher URL: https://{domain}/{first-tag-slug}/{story-slug}
const getLiveUrl = (story) => {
  if (!story.publication_domain || !story.slug) return null;
  const tag = story.tags?.[0]
    ? story.tags[0].toLowerCase().replace(/\s+/g, '-')
    : 'news';
  return `https://${story.publication_domain}/${tag}/${story.slug}`;
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full text-xs px-2 py-0.5 font-medium whitespace-nowrap ${STATUS_STYLES[status] || STATUS_STYLES.draft}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function OriginIcon({ origin }) {
  if (origin === 'eventflow') {
    return <Workflow size={13} className="text-violet-500 shrink-0" title="Event Flow" />;
  }
  return <PenLine size={13} className="text-slate-500 shrink-0" title="Casted" />;
}

function computeBackdate(list, opts) {
  const ordered = [...list].sort((a, b) => {
    const da = new Date(a.created_at).getTime();
    const db = new Date(b.created_at).getTime();
    return opts.order === 'newest' ? db - da : da - db;
  });
  const N = ordered.length;
  const out = [];

  if (opts.mode === 'same') {
    if (!opts.date) return [];
    const base = new Date(`${opts.date}T${opts.time || '09:00'}`);
    if (isNaN(base.getTime())) return [];
    ordered.forEach((story, i) => {
      const d = new Date(base);
      if (opts.space) d.setMinutes(d.getMinutes() + i * (opts.spaceMinutes || 10));
      out.push({ story, date: d });
    });
    return out;
  }

  if (!opts.startDate || !opts.endDate) return [];
  const start = new Date(`${opts.startDate}T00:00`);
  const end   = new Date(`${opts.endDate}T00:00`);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return [];
  const h1 = Math.max(0, Math.min(23, opts.hourStart ?? 9));
  const h2 = Math.max(h1, Math.min(23, opts.hourEnd ?? 17));
  const numDays = Math.floor((end - start) / 86400000) + 1;
  const perDay  = Math.max(1, Math.ceil(N / numDays));
  const slots = [];
  for (let day = 0; day < numDays && slots.length < N; day++) {
    for (let k = 0; k < perDay && slots.length < N; k++) {
      const frac = perDay > 1 ? k / (perDay - 1) : 0.5;
      const mins = Math.round(frac * (h2 - h1) * 60);
      const d = new Date(start);
      d.setDate(start.getDate() + day);
      d.setHours(h1, 0, 0, 0);
      d.setMinutes(d.getMinutes() + mins);
      slots.push(d);
    }
  }
  ordered.forEach((story, i) => out.push({ story, date: slots[i] }));
  return out;
}

const pad2 = (n) => String(n).padStart(2, '0');

const shiftYmd = (ymd, days) => {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + days);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
};
function computeSchedule(list, opts) {
  const ordered = [...list].sort((a, b) => {
    const da = new Date(a.created_at).getTime();
    const db = new Date(b.created_at).getTime();
    return opts.order === 'newest' ? db - da : da - db;
  });
  const N = ordered.length;
  const out = [];

  if (opts.mode === 'same') {
    if (!opts.date) return [];
    const baseIso = ukInputToISO(`${opts.date}T${opts.time || '09:00'}`);
    if (!baseIso) return [];
    const baseMs = new Date(baseIso).getTime();
    ordered.forEach((story, i) => {
      const ms = baseMs + (opts.space ? i * (opts.spaceMinutes || 10) * 60000 : 0);
      out.push({ story, iso: new Date(ms).toISOString() });
    });
    return out;
  }

  if (!opts.startDate || !opts.endDate) return [];
  const start = new Date(`${opts.startDate}T00:00:00Z`).getTime();
  const end   = new Date(`${opts.endDate}T00:00:00Z`).getTime();
  if (isNaN(start) || isNaN(end) || end < start) return [];
  const h1 = Math.max(0, Math.min(23, opts.hourStart ?? 9));
  const h2 = Math.max(h1, Math.min(23, opts.hourEnd ?? 17));
  const numDays = Math.floor((end - start) / 86400000) + 1;
  const perDay  = Math.max(1, Math.ceil(N / numDays));
  const slots = [];
  for (let day = 0; day < numDays && slots.length < N; day++) {
    const ymd = shiftYmd(opts.startDate, day);
    if (!ymd) break;
    for (let k = 0; k < perDay && slots.length < N; k++) {
      const frac = perDay > 1 ? k / (perDay - 1) : 0.5;
      const mins = h1 * 60 + Math.round(frac * (h2 - h1) * 60);
      slots.push(ukInputToISO(`${ymd}T${pad2(Math.floor(mins / 60))}:${pad2(mins % 60)}`));
    }
  }
  ordered.forEach((story, i) => out.push({ story, iso: slots[i] }));
  return out;
}

function BulkScheduleModal({ stories, skipped, onClose, onApplied }) {
  const [mode, setMode]         = useState('same');
  const [date, setDate]         = useState('');
  const [time, setTime]         = useState('09:00');
  const [space, setSpace]       = useState(true);
  const [spaceMinutes, setSpaceMinutes] = useState(10);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [hourStart, setHourStart] = useState(9);
  const [hourEnd, setHourEnd]     = useState(17);
  const [order, setOrder]         = useState('oldest');
  const [busy, setBusy]           = useState(false);

  const assignments = computeSchedule(stories, {
    mode, date, time, space, spaceMinutes,
    startDate, endDate, hourStart, hourEnd, order,
  });

  const complete = assignments.length === stories.length && assignments.every(a => !!a.iso);
  const times    = complete ? assignments.map(a => new Date(a.iso).getTime()) : [];
  const minT     = times.length ? Math.min(...times) : null;
  const maxT     = times.length ? Math.max(...times) : null;
  const inPast   = minT !== null && minT <= Date.now() + 60000;
  const valid    = complete && !inPast && stories.length > 0;

  const apply = async () => {
    setBusy(true);
    const results = await Promise.allSettled(assignments.map(a =>
      api.post(
        `/stories/${a.story.id}/${a.story.status === 'scheduled' ? 'reschedule' : 'schedule'}`,
        { scheduled_for: a.iso }
      )
    ));
    const failures = [];
    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        failures.push({
          headline: assignments[i].story.headline || 'Untitled',
          detail:   r.reason?.response?.data?.detail || 'Failed',
        });
      }
    });
    const ok = assignments.length - failures.length;
    setBusy(false);
    onApplied(ok, failures);
  };

  const fieldCls = 'w-full bg-white text-[#1F1A17] border border-border rounded-md h-9 px-3 text-sm focus:outline-none focus:border-[#1F1A17]';

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Clock size={18} /> Schedule {stories.length} {stories.length === 1 ? 'story' : 'stories'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Each story stays off the live site until its time, then publishes on its own.
          </DialogDescription>
        </DialogHeader>

        <LondonClock />

        {skipped > 0 && (
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-700">
            {skipped} selected {skipped === 1 ? 'story is' : 'stories are'} not schedulable and will be left alone.
            Only stories in review, approved, unpublished or already scheduled can be queued.
          </div>
        )}

        <div className="flex gap-1 bg-[#FFF4E8] p-1 rounded-lg border border-border w-fit">
          {[['same', 'One time'], ['range', 'Across a range']].map(([id, lbl]) => (
            <button key={id} onClick={() => setMode(id)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${mode === id ? 'bg-[#FFE4C4] text-[#1F1A17]' : 'text-muted-foreground hover:text-foreground'}`}>
              {lbl}
            </button>
          ))}
        </div>

        {mode === 'same' ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Date</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Time</label>
                <input type="time" value={time} onChange={e => setTime(e.target.value)} className={fieldCls} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#6E6258]">
              <input type="checkbox" checked={space} onChange={e => setSpace(e.target.checked)} className="h-4 w-4 accent-[#1F1A17]" />
              Stagger them so they don't all publish at once
            </label>
            {space && (
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Minutes between stories</label>
                <input type="number" min="1" value={spaceMinutes} onChange={e => setSpaceMinutes(parseInt(e.target.value) || 1)} className={`${fieldCls} w-32`} />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Start date</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">End date</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Earliest hour</label>
                <input type="number" min="0" max="23" value={hourStart} onChange={e => setHourStart(parseInt(e.target.value) || 0)} className={fieldCls} />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Latest hour</label>
                <input type="number" min="0" max="23" value={hourEnd} onChange={e => setHourEnd(parseInt(e.target.value) || 0)} className={fieldCls} />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#6E6258] mb-1 block">Ordering</label>
              <Select value={order} onValueChange={setOrder}>
                <SelectTrigger className={fieldCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="oldest" className="text-foreground focus:bg-[#FFF4E8]">Oldest selected first</SelectItem>
                  <SelectItem value="newest" className="text-foreground focus:bg-[#FFF4E8]">Newest selected first</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className={`rounded-lg border px-3 py-2 text-sm ${
          inPast ? 'bg-red-50 border-red-200 text-red-600' : 'bg-[#FFF4E8] border-border text-[#6E6258]'
        }`}>
          {!complete
            ? 'Fill in the fields above to preview the publication times.'
            : inPast
              ? 'The earliest slot has already passed — move the date or time forward.'
              : minT === maxT
                ? `All ${stories.length} publish ${fmtUkLong(new Date(minT).toISOString())} — ${relativeTo(new Date(minT).toISOString())}.`
                : `${stories.length} stories publish between ${fmtUkLong(new Date(minT).toISOString())} and ${fmtUkLong(new Date(maxT).toISOString())}.`
          }
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-muted-foreground hover:text-foreground">Cancel</Button>
          <Button onClick={apply} disabled={!valid || busy} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">
            {busy && <Loader2 size={14} className="animate-spin mr-1.5" />}
            {busy ? 'Scheduling…' : 'Schedule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BackdateModal({ stories, onClose, onApplied }) {
  const [mode, setMode]         = useState('same');
  const [date, setDate]         = useState('');
  const [time, setTime]         = useState('09:00');
  const [space, setSpace]       = useState(false);
  const [spaceMinutes, setSpaceMinutes] = useState(10);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate]     = useState('');
  const [hourStart, setHourStart] = useState(9);
  const [hourEnd, setHourEnd]     = useState(17);
  const [order, setOrder]         = useState('oldest');
  const [busy, setBusy]           = useState(false);

  const assignments = computeBackdate(stories, {
    mode, date, time, space, spaceMinutes,
    startDate, endDate, hourStart, hourEnd, order,
  });

  const valid = assignments.length === stories.length && assignments.every(a => a.date && !isNaN(a.date.getTime()));
  const dates = valid ? assignments.map(a => a.date.getTime()) : [];
  const minD  = dates.length ? new Date(Math.min(...dates)) : null;
  const maxD  = dates.length ? new Date(Math.max(...dates)) : null;

  const apply = async () => {
    setBusy(true);
    try {
      const items = assignments.map(a => ({ id: a.story.id, public_display_at: a.date.toISOString() }));
      const res = await api.post('/stories/bulk-public-date', { items });
      const { updated, failed } = res.data;
      (failed?.length ? toast.error : toast.success)(
        `${updated} public date${updated === 1 ? '' : 's'} set${failed?.length ? `, ${failed.length} failed` : ''}`
      );
      onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set public dates');
    } finally {
      setBusy(false);
    }
  };

  const fieldCls = 'w-full bg-white text-[#1F1A17] border border-border rounded-md h-9 px-3 text-sm focus:outline-none focus:border-[#1F1A17]';

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <CalendarClock size={18} /> Set public date — {stories.length} {stories.length === 1 ? 'story' : 'stories'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Sets the date shown publicly and the ordering in listings. Internal timestamps are untouched.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 bg-[#FFF4E8] p-1 rounded-lg border border-border w-fit">
          {[['same', 'One date'], ['range', 'Across a range']].map(([id, lbl]) => (
            <button key={id} onClick={() => setMode(id)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${mode === id ? 'bg-[#FFE4C4] text-[#1F1A17]' : 'text-muted-foreground hover:text-foreground'}`}>
              {lbl}
            </button>
          ))}
        </div>

        {mode === 'same' ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Date</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Time</label>
                <input type="time" value={time} onChange={e => setTime(e.target.value)} className={fieldCls} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#6E6258]">
              <input type="checkbox" checked={space} onChange={e => setSpace(e.target.checked)} className="h-4 w-4 accent-[#1F1A17]" />
              Space times apart so stories don't share an identical timestamp
            </label>
            {space && (
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Minutes between stories</label>
                <input type="number" min="1" value={spaceMinutes} onChange={e => setSpaceMinutes(parseInt(e.target.value) || 1)} className={`${fieldCls} w-32`} />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Start date</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">End date</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Earliest hour</label>
                <input type="number" min="0" max="23" value={hourStart} onChange={e => setHourStart(parseInt(e.target.value) || 0)} className={fieldCls} />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Latest hour</label>
                <input type="number" min="0" max="23" value={hourEnd} onChange={e => setHourEnd(parseInt(e.target.value) || 0)} className={fieldCls} />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#6E6258] mb-1 block">Ordering</label>
              <Select value={order} onValueChange={setOrder}>
                <SelectTrigger className={fieldCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="oldest" className="text-foreground focus:bg-[#FFF4E8]">Oldest selected first</SelectItem>
                  <SelectItem value="newest" className="text-foreground focus:bg-[#FFF4E8]">Newest selected first</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <div className="rounded-lg bg-[#FFF4E8] border border-border px-3 py-2 text-sm text-[#6E6258]">
          {valid && minD && maxD
            ? `${stories.length} ${stories.length === 1 ? 'story' : 'stories'} will be assigned public dates between ${fmtUK(minD)} and ${fmtUK(maxD)}.`
            : 'Fill in the fields above to preview the assigned dates.'}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-muted-foreground hover:text-foreground">Cancel</Button>
          <Button onClick={apply} disabled={!valid || busy} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">
            {busy ? 'Applying…' : 'Apply public dates'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ColumnsMenu({ hidden, onToggle, onReset, density, onDensity }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(o => !o)}
        className={`border-border text-foreground hover:bg-[#FFF4E8] gap-1.5 h-10 ${open ? 'bg-[#FFE4C4] border-[#C9B49A]' : ''}`}
      >
        <Columns3 size={14} />
        Columns
        {hidden.size > 0 && <span className="text-[11px] text-[#9C8E84]">{COLUMNS.length - hidden.size}/{COLUMNS.length}</span>}
      </Button>

      {open && (
        <div className="absolute right-0 mt-1 z-50 w-56 rounded-lg border border-border bg-white shadow-lg p-1.5 animate-in fade-in-0 zoom-in-95 duration-150">
          <p className="px-2 py-1 text-[11px] uppercase tracking-wide text-[#9C8E84] font-semibold">Density</p>
          <div className="flex gap-1 p-1">
            {[['compact', 'Compact'], ['comfortable', 'Comfortable']].map(([id, lbl]) => (
              <button
                key={id}
                onClick={() => onDensity(id)}
                className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${density === id ? 'bg-[#FFE4C4] text-[#1F1A17]' : 'text-[#6E6258] hover:bg-[#FFF4E8]'}`}
              >
                {lbl}
              </button>
            ))}
          </div>

          <p className="px-2 pt-2 pb-1 text-[11px] uppercase tracking-wide text-[#9C8E84] font-semibold">Show columns</p>
          <div className="max-h-64 overflow-y-auto">
            {COLUMNS.map(c => {
              const on = !hidden.has(c.id);
              return (
                <button
                  key={c.id}
                  onClick={() => onToggle(c.id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-foreground hover:bg-[#FFF4E8] transition-colors"
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-[#1F1A17] border-[#1F1A17]' : 'border-[#C9B49A]'}`}>
                    {on && <Check size={11} className="text-[#FFE4C4]" />}
                  </span>
                  {c.label}
                </button>
              );
            })}
          </div>

          {hidden.size > 0 && (
            <button onClick={onReset} className="w-full text-left px-2 py-1.5 mt-1 rounded-md text-xs text-[#6E6258] hover:bg-[#FFF4E8] border-t border-border">
              Show all columns
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function StoriesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isCaster        = user?.role === 'caster';
  const isAdminOrEditor = ['admin', 'editor'].includes(user?.role);

  const initOrigin = searchParams.get('origin') || (isCaster ? 'all' : 'casted');
  const initStatus = searchParams.get('status') || (isCaster ? 'all' : 'review');
  const initSort   = searchParams.get('sort')   || DEFAULT_SORT[initStatus] || 'updated';

  const [origin, setOrigin]           = useState(initOrigin);
  const [status, setStatus]           = useState(initStatus);
  const [sort, setSort]               = useState(initSort);
  const [search, setSearch]           = useState(searchParams.get('search') || '');
  const [filters, setFilters]         = useState(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);

  const [stories, setStories]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [counts, setCounts]         = useState(null);
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [selected, setSelected]     = useState(() => new Set());
  const [busyId, setBusyId]         = useState(null);
  const [bulkBusy, setBulkBusy]     = useState(false);
  const [confirm, setConfirm]       = useState(null);
  const [showBackdate, setShowBackdate] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [bulkResults, setBulkResults]   = useState(null);

  const [hiddenCols, setHiddenCols] = useState(() => new Set(readStored(LS_COLS, [])));
  const [density, setDensity]       = useState(() => readStored(LS_DENSITY, 'comfortable'));

  const [clients, setClients]           = useState([]);
  const [publications, setPublications] = useState([]);
  const [allTags, setAllTags]           = useState([]);
  const [casters, setCasters]           = useState([]);
  const [storyTypes, setStoryTypes]     = useState([]);
  const [refLoaded, setRefLoaded]       = useState(false);

  const originRef   = useRef(origin);
  const statusRef   = useRef(status);
  const sortRef     = useRef(sort);
  const filtersRef  = useRef(filters);
  const searchRef   = useRef(search);
  const clientsRef  = useRef(clients);
  const pageRef     = useRef(page);
  const searchInput = useRef(null);
  const debounceRef = useRef(null);
  const lastIdxRef  = useRef(null);

  useEffect(() => { filtersRef.current = filters; }, [filters]);
  useEffect(() => { clientsRef.current = clients; }, [clients]);
  useEffect(() => { pageRef.current    = page;    }, [page]);

  useEffect(() => { writeStored(LS_COLS, [...hiddenCols]); }, [hiddenCols]);
  useEffect(() => { writeStored(LS_DENSITY, density); }, [density]);

  const showCol = (id) => !hiddenCols.has(id);

  const subtabs = isCaster ? CASTER_SUBTABS : (SUBTABS[origin] || SUBTABS.all);
  const countScope = isCaster ? 'all' : origin;
  const countFor = (scope, sub) => {
    const c = counts?.[scope];
    if (!c) return null;
    return sub === 'all' ? c.all : (c[sub] ?? 0);
  };

  useEffect(() => {
    if (!user) return;
    const loadRef = async () => {
      try {
        const [c, p, t, st] = await Promise.all([
          api.get('/clients'),
          api.get('/publications'),
          api.get('/tags'),
          api.get('/story-types'),
        ]);
        setClients(c.data);
        setPublications(p.data);
        setAllTags(t.data);
        setStoryTypes(st.data);
        if (isAdminOrEditor) {
          const u = await api.get('/users');
          setCasters(u.data.filter(usr => ['caster', 'editor', 'admin'].includes(usr.role)));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setRefLoaded(true);
      }
    };
    loadRef();
  }, [user, isAdminOrEditor]);

  const syncUrl = useCallback(() => {
    const sp = {};
    if (originRef.current && originRef.current !== 'all') sp.origin = originRef.current;
    if (statusRef.current && statusRef.current !== 'all') sp.status = statusRef.current;
    if (sortRef.current) sp.sort = sortRef.current;
    if (searchRef.current) sp.search = searchRef.current;
    setSearchParams(sp, { replace: true });
  }, [setSearchParams]);

  const fetchStories = useCallback(async (pageNum = 1) => {
    // Suppressed reads feed_items, not stories. That tab fetches its own data.
    if (statusRef.current === 'suppressed') return;

    setLoading(true);
    try {
      const f  = filtersRef.current;
      const cl = clientsRef.current;

      const params = { page: pageNum, page_size: PAGE_SIZE, sort: sortRef.current };
      if (originRef.current !== 'all') params.origin = originRef.current;
      if (statusRef.current !== 'all') params.status = statusRef.current;
      if (searchRef.current)          params.search = searchRef.current;

      if (f.story_type !== 'all')     params.story_type     = f.story_type;
      if (f.tag !== 'all')            params.tag            = f.tag;
      if (f.caster_id !== 'all')      params.caster_id      = f.caster_id;
      if (f.publication_id !== 'all') params.publication_id = f.publication_id;
      if (f.geography)                params.geography      = f.geography;
      if (f.date_from)                params.date_from      = f.date_from;
      if (f.date_to)                  params.date_to        = f.date_to;
      if (f.date_published_from)      params.date_published_from = f.date_published_from;
      if (f.date_published_to)        params.date_published_to   = f.date_published_to;
      if (f.client_id !== 'all') {
        const client = cl.find(c => c.id === f.client_id);
        if (client) params.client_name = client.name;
      }
      if (f.project_name) params.project_name = f.project_name;

      const res = await api.get('/stories', { params });
      setStories(res.data.stories);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setPage(res.data.page);
      setSelected(new Set());
      lastIdxRef.current = null;
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await api.get('/stories/counts');
      setCounts(res.data);
    } catch {
      /* badges are non-critical */
    }
  }, []);

  useEffect(() => {
    if (refLoaded) {
      searchRef.current = search;
      fetchStories(1);
      fetchCounts();
    }
  }, [refLoaded]);

  const refresh = async () => {
    await fetchStories(pageRef.current);
    fetchCounts();
  };

  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
      if (e.key === '/' && !typing) {
        e.preventDefault();
        searchInput.current?.focus();
      }
      if (e.key === 'Escape' && !typing) setSelected(new Set());
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const changeParent = (newOrigin) => {
    originRef.current = newOrigin;
    setOrigin(newOrigin);
    const subs = isCaster ? CASTER_SUBTABS : (SUBTABS[newOrigin] || SUBTABS.all);
    const nextStatus = subs.includes(statusRef.current) ? statusRef.current : 'all';
    statusRef.current = nextStatus;
    setStatus(nextStatus);
    sortRef.current = DEFAULT_SORT[nextStatus] || 'updated';
    setSort(sortRef.current);
    setSelected(new Set());
    syncUrl();
    fetchStories(1);
  };

  const changeStatus = (newStatus) => {
    statusRef.current = newStatus;
    setStatus(newStatus);
    sortRef.current = DEFAULT_SORT[newStatus] || 'updated';
    setSort(sortRef.current);
    setSelected(new Set());
    syncUrl();
    fetchStories(1);
  };

  const changeSort = (v) => {
    sortRef.current = v;
    setSort(v);
    syncUrl();
    fetchStories(1);
  };

  const clickSort = (sorts) => {
    if (!sorts?.length) return;
    const idx = sorts.indexOf(sortRef.current);
    changeSort(sorts[(idx + 1) % sorts.length]);
  };

  const runSearch = (value) => {
    searchRef.current = value;
    syncUrl();
    fetchStories(1);
  };

  const onSearchChange = (value) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (refLoaded) runSearch(value);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    clearTimeout(debounceRef.current);
    runSearch(search);
  };

  const clearSearch = () => {
    clearTimeout(debounceRef.current);
    setSearch('');
    runSearch('');
    searchInput.current?.focus();
  };

  const setFilter = (key, val) => {
    const next = { ...filtersRef.current, [key]: val };
    filtersRef.current = next;
    setFilters(next);
  };

  const applyFilters = () => {
    fetchStories(1);
  };

  const removeFilter = (key) => {
    setFilter(key, FILTER_DEFAULT(key));
    if (key === 'client_id') setFilter('project_name', '');
    fetchStories(1);
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => v !== FILTER_DEFAULT(k)).length;

  const clearFilters = () => {
    filtersRef.current = emptyFilters;
    setFilters(emptyFilters);
    fetchStories(1);
  };

  const chips = useMemo(() => {
    const f = filters;
    const out = [];
    const push = (key, label) => out.push({ key, label });
    if (f.client_id !== 'all')      push('client_id', `Client: ${clients.find(c => c.id === f.client_id)?.name || f.client_id}`);
    if (f.project_name)             push('project_name', `Project: ${f.project_name}`);
    if (f.publication_id !== 'all') push('publication_id', `Publication: ${publications.find(p => p.id === f.publication_id)?.name || f.publication_id}`);
    if (f.story_type !== 'all')     push('story_type', `Type: ${storyTypeLabels[f.story_type] || f.story_type}`);
    if (f.tag !== 'all')            push('tag', `Tag: ${f.tag}`);
    if (f.caster_id !== 'all')      push('caster_id', `User: ${casters.find(c => c.id === f.caster_id)?.name || f.caster_id}`);
    if (f.geography)                push('geography', `Geography: ${f.geography}`);
    if (f.date_from)                push('date_from', `Created from ${f.date_from}`);
    if (f.date_to)                  push('date_to', `Created to ${f.date_to}`);
    if (f.date_published_from)      push('date_published_from', `Published from ${f.date_published_from}`);
    if (f.date_published_to)        push('date_published_to', `Published to ${f.date_published_to}`);
    return out;
  }, [filters, clients, publications, casters]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    fetchStories(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const doAction = async (id, action) => {
    setBusyId(id);
    try {
      if (action === 'delete')         await api.delete(`/stories/${id}`);
      else if (action === 'permanent') await api.delete(`/stories/${id}/permanent`);
      else                             await api.post(`/stories/${id}/${action}`, {});
      toast.success(`Story ${VERB[action] || action}`);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setBusyId(null);
      setConfirm(null);
    }
  };

  const askDelete = (story) => setConfirm({
    title: 'Delete story',
    body: `Move "${story.headline || 'Untitled'}" to the Deleted tab? It's kept for 7 days, then permanently removed.`,
    confirmLabel: 'Delete',
    onConfirm: () => doAction(story.id, 'delete'),
  });

  const askPermanent = (story) => setConfirm({
    title: 'Delete permanently',
    body: `Permanently delete "${story.headline || 'Untitled'}"? This cannot be undone and the source article will not be regenerated.`,
    confirmLabel: 'Delete permanently',
    onConfirm: () => doAction(story.id, 'permanent'),
  });

  const askUnpublish = (story) => setConfirm({
    title: 'Unpublish story',
    body: `Take "${story.headline || 'Untitled'}" off the live site? It can be republished later.`,
    confirmLabel: 'Unpublish',
    onConfirm: () => doAction(story.id, 'unpublish'),
  });

  const askCancelSchedule = (story) => setConfirm({
    title: 'Cancel schedule',
    body: `Take "${story.headline || 'Untitled'}" off the schedule? It returns to where it was before and won't publish automatically.`,
    confirmLabel: 'Cancel schedule',
    onConfirm: () => doAction(story.id, 'cancel-schedule'),
  });

  const runBulk = async (action) => {
    const ids  = [...selected];
    const byId = new Map(stories.map(s => [s.id, s]));
    setBulkBusy(true);
    const results = await Promise.allSettled(ids.map(id =>
      action === 'delete'    ? api.delete(`/stories/${id}`) :
      action === 'permanent' ? api.delete(`/stories/${id}/permanent`) :
                               api.post(`/stories/${id}/${action}`, {})
    ));
    const failures = [];
    results.forEach((r, i) => {
      if (r.status === 'rejected') {
        failures.push({
          headline: byId.get(ids[i])?.headline || 'Untitled',
          detail:   r.reason?.response?.data?.detail || 'Failed',
        });
      }
    });
    const ok = ids.length - failures.length;
    setSelected(new Set());
    setBulkBusy(false);
    setConfirm(null);
    await refresh();
    if (action === 'approve' && failures.length) {
      setBulkResults({ ok, failures });
    } else {
      (failures.length ? toast.error : toast.success)(
        `${ok} ${VERB[action] || action}${failures.length ? `, ${failures.length} failed` : ''}`
      );
    }
  };

  const askBulk = (action, label) => {
    if (!['delete', 'unpublish', 'permanent', 'cancel-schedule'].includes(action)) return runBulk(action);
    const n = `${selected.size} ${selected.size === 1 ? 'story' : 'stories'}`;
    setConfirm({
      title: `${label} ${n}`,
      body: action === 'permanent'
        ? 'This permanently deletes the selected stories and cannot be undone. Their source articles will not be regenerated.'
        : action === 'delete'
          ? 'The selected stories move to the Deleted tab, kept for 7 days then permanently removed.'
          : action === 'cancel-schedule'
            ? 'The selected stories come off the schedule and return to where they were before.'
            : 'This takes the selected stories off the live site.',
      confirmLabel: label,
      onConfirm: () => runBulk(action),
    });
  };

  const toggleSelect = (id, idx, shift) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (shift && lastIdxRef.current !== null && lastIdxRef.current !== idx) {
        const lo = Math.min(lastIdxRef.current, idx);
        const hi = Math.max(lastIdxRef.current, idx);
        const turnOn = !next.has(id);
        for (let i = lo; i <= hi; i++) {
          const sid = stories[i]?.id;
          if (!sid) continue;
          if (turnOn) next.add(sid);
          else next.delete(sid);
        }
      } else if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
    lastIdxRef.current = idx;
  };

  const allOnPageSelected = stories.length > 0 && stories.every(s => selected.has(s.id));

  const toggleSelectAll = () => setSelected(prev => {
    const next = new Set(prev);
    if (stories.every(s => next.has(s.id))) stories.forEach(s => next.delete(s.id));
    else stories.forEach(s => next.add(s.id));
    lastIdxRef.current = null;
    return next;
  });

  const selectedStories   = stories.filter(s => selected.has(s.id) && !s.deleted_at);
  const schedulablePicked = selectedStories.filter(s => SCHEDULABLE.includes(s.status));
  const skippedPicked     = selectedStories.length - schedulablePicked.length;

  const uniqueTags = [...new Set(allTags.map(t => t.name))].sort();
  const clientProjects = filters.client_id !== 'all'
    ? (clients.find(c => c.id === filters.client_id)?.projects || [])
    : [];

  const compact = density === 'compact';
  const cellY   = compact ? 'py-1.5' : 'py-3';
  const thBase  = `px-3 ${compact ? 'py-2' : 'py-3'} text-left text-[11px] font-semibold text-[#6E6258] uppercase tracking-wide whitespace-nowrap`;
  const tdBase  = `px-3 ${cellY} align-middle whitespace-nowrap`;
  const iconBtn = 'h-7 w-7 inline-flex items-center justify-center rounded-md transition-colors disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9B49A]';

  const renderActions = (story) => {
    if (!isAdminOrEditor && !isCaster) return <span className="text-[#C9B49A]">—</span>;
    const busy    = busyId === story.id;
    const liveUrl = getLiveUrl(story);
    const st      = story.status;

    if (busy) {
      return (
        <div className="flex items-center justify-end pr-1">
          <Loader2 size={15} className="animate-spin text-[#9C8E84]" />
        </div>
      );
    }

    if (isCaster) {
      return (
        <div className="flex items-center gap-0.5 justify-end" onClick={e => e.stopPropagation()}>
          {['draft', 'refused'].includes(st) && (
            <>
              <button title="Edit" onClick={e => { e.stopPropagation(); navigate(`/stories/${story.id}`); }} className={`${iconBtn} text-[#6E6258] hover:bg-[#FFF4E8]`}>
                <Edit3 size={15} />
              </button>
              <button title="Submit for Review" onClick={e => { e.stopPropagation(); doAction(story.id, 'submit'); }} className={`${iconBtn} text-[#1F1A17] hover:bg-[#FFF4E8]`}>
                <Send size={15} />
              </button>
            </>
          )}
        </div>
      );
    }

    if (story.deleted_at) {
      return (
        <div className="flex items-center gap-0.5 justify-end" onClick={e => e.stopPropagation()}>
          <button title="Restore" onClick={e => { e.stopPropagation(); doAction(story.id, 'restore'); }} className={`${iconBtn} text-emerald-600 hover:bg-emerald-50`}>
            <RotateCcw size={15} />
          </button>
          <button title="Delete permanently" onClick={e => { e.stopPropagation(); askPermanent(story); }} className={`${iconBtn} text-red-500 hover:bg-red-50`}>
            <Trash2 size={15} />
          </button>
        </div>
      );
    }

    return (
      <div className="flex items-center gap-0.5 justify-end" onClick={e => e.stopPropagation()}>
        {st === 'draft' && (
          <>
            <button title="Edit" onClick={e => { e.stopPropagation(); navigate(`/stories/${story.id}`); }} className={`${iconBtn} text-[#6E6258] hover:bg-[#FFF4E8]`}>
              <Edit3 size={15} />
            </button>
            <button title="Submit for Review" onClick={e => { e.stopPropagation(); doAction(story.id, 'submit'); }} className={`${iconBtn} text-[#1F1A17] hover:bg-[#FFF4E8]`}>
              <Send size={15} />
            </button>
          </>
        )}
        {st === 'review' && (
          <>
            <button title="Approve & Publish" onClick={e => { e.stopPropagation(); doAction(story.id, 'approve'); }} className={`${iconBtn} text-emerald-600 hover:bg-emerald-50`}>
              <CheckCircle size={15} />
            </button>
            <button title="Refuse (opens story)" onClick={e => { e.stopPropagation(); navigate(`/stories/${story.id}`); }} className={`${iconBtn} text-red-500 hover:bg-red-50`}>
              <XCircle size={15} />
            </button>
            <button title="Schedule" onClick={e => { e.stopPropagation(); navigate(`/stories/${story.id}?tab=schedule`); }} className={`${iconBtn} text-[#6E6258] hover:bg-[#FFF4E8]`}>
              <CalendarClock size={15} />
            </button>
            <button title="Save as Draft" onClick={e => { e.stopPropagation(); doAction(story.id, 'save-draft'); }} className={`${iconBtn} text-[#6E6258] hover:bg-[#FFF4E8]`}>
              <Save size={15} />
            </button>
          </>
        )}
        {st === 'scheduled' && (
          <>
            {story.last_publish_error && (
              <button title="Retry publish" onClick={e => { e.stopPropagation(); doAction(story.id, 'retry-publish'); }} className={`${iconBtn} text-red-500 hover:bg-red-50`}>
                <RefreshCw size={15} />
              </button>
            )}
            <button title="Publish now" onClick={e => { e.stopPropagation(); doAction(story.id, 'publish'); }} className={`${iconBtn} text-emerald-600 hover:bg-emerald-50`}>
              <Rocket size={15} />
            </button>
            <button title="Reschedule" onClick={e => { e.stopPropagation(); navigate(`/stories/${story.id}?tab=schedule`); }} className={`${iconBtn} text-[#6E6258] hover:bg-[#FFF4E8]`}>
              <CalendarClock size={15} />
            </button>
            <button title="Cancel schedule" onClick={e => { e.stopPropagation(); askCancelSchedule(story); }} className={`${iconBtn} text-[#6E6258] hover:bg-[#FFF4E8]`}>
              <X size={15} />
            </button>
          </>
        )}
        {st === 'published' && (
          <>
            {liveUrl && (
              <button title="Visit live" onClick={e => { e.stopPropagation(); window.open(liveUrl, '_blank', 'noopener,noreferrer'); }} className={`${iconBtn} text-emerald-700 hover:bg-emerald-50`}>
                <ExternalLink size={15} />
              </button>
            )}
            <button title="Unpublish" onClick={e => { e.stopPropagation(); askUnpublish(story); }} className={`${iconBtn} text-orange-600 hover:bg-orange-50`}>
              <BookX size={15} />
            </button>
          </>
        )}
        {st === 'unpublished' && (
          <>
            <button title="Republish" onClick={e => { e.stopPropagation(); doAction(story.id, 'publish'); }} className={`${iconBtn} text-emerald-600 hover:bg-emerald-50`}>
              <Rocket size={15} />
            </button>
            <button title="Schedule" onClick={e => { e.stopPropagation(); navigate(`/stories/${story.id}?tab=schedule`); }} className={`${iconBtn} text-[#6E6258] hover:bg-[#FFF4E8]`}>
              <CalendarClock size={15} />
            </button>
          </>
        )}
        {st === 'refused' && (
          <button title="Reopen for Review" onClick={e => { e.stopPropagation(); doAction(story.id, 'submit'); }} className={`${iconBtn} text-[#1F1A17] hover:bg-[#FFF4E8]`}>
            <Send size={15} />
          </button>
        )}
        {['failed', 'needs_attention'].includes(st) && (
          <button title="Open" onClick={e => { e.stopPropagation(); navigate(`/stories/${story.id}`); }} className={`${iconBtn} text-[#6E6258] hover:bg-[#FFF4E8]`}>
            <Edit3 size={15} />
          </button>
        )}
        <button title="Delete" onClick={e => { e.stopPropagation(); askDelete(story); }} className={`${iconBtn} text-red-500 hover:bg-red-50`}>
          <Trash2 size={15} />
        </button>
      </div>
    );
  };

  const bulkFor = () => {
    if (!isAdminOrEditor) return [];
    switch (status) {
      case 'review':      return [{ action: 'approve', label: 'Approve & Publish', icon: CheckCircle }, { action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
      case 'draft':       return [{ action: 'submit', label: 'Submit', icon: Send }, { action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
      case 'scheduled':   return [{ action: 'publish', label: 'Publish now', icon: Rocket }, { action: 'cancel-schedule', label: 'Cancel schedule', icon: X }, { action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
      case 'published':   return [{ action: 'unpublish', label: 'Unpublish', icon: BookX, danger: true }, { action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
      case 'unpublished': return [{ action: 'publish', label: 'Republish', icon: Rocket }, { action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
      case 'refused':     return [{ action: 'submit', label: 'Reopen', icon: Send }, { action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
      case 'deleted':     return [{ action: 'restore', label: 'Restore', icon: RotateCcw }, { action: 'permanent', label: 'Delete permanently', icon: Trash2, danger: true }];
      default:            return [{ action: 'delete', label: 'Delete', icon: Trash2, danger: true }];
    }
  };

  const emptyCopy = () => {
    const draftCount  = countFor(countScope, 'draft');
    const reviewCount = countFor(countScope, 'review');
    if (searchRef.current) return { title: `Nothing matches "${searchRef.current}"`, hint: 'Try a shorter search, or clear the filters.' };
    if (activeFilterCount) return { title: 'No stories match these filters', hint: 'Loosen or clear a filter to widen the results.' };
    switch (status) {
      case 'review':      return { title: 'Nothing waiting for review', hint: draftCount ? `${draftCount} draft${draftCount === 1 ? '' : 's'} still to be submitted.` : 'Drafts appear here once a caster submits them.' };
      case 'draft':       return { title: 'No drafts', hint: reviewCount ? `${reviewCount} stor${reviewCount === 1 ? 'y is' : 'ies are'} in review.` : 'Start one with New Story.' };
      case 'scheduled':   return { title: 'Nothing scheduled', hint: 'Stories queued for a future publication time appear here.' };
      case 'published':   return { title: 'Nothing published yet', hint: 'Approved stories land here and go live on the site.' };
      case 'unpublished': return { title: 'Nothing unpublished', hint: 'Stories taken off the live site show up here.' };
      case 'refused':     return { title: 'No refused stories', hint: 'Refused stories can be reopened for review from here.' };
      case 'attention':   return { title: 'Nothing needs attention', hint: 'Failed or incomplete EventFlow stories appear here.' };
      case 'deleted':     return { title: 'Deleted is empty', hint: 'Deleted stories stay here for 7 days before being removed.' };
      default:            return { title: 'No stories here', hint: 'Start one with New Story.' };
    }
  };

  const showOriginIcon = !isCaster;
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd   = Math.min(page * PAGE_SIZE, total);
  const firstLoad  = loading && stories.length === 0;
  const refetching = loading && stories.length > 0;

  const sortCaret = (sorts) => {
    if (!sorts?.includes(sort)) return null;
    const Icon = ASC_SORTS.includes(sort) ? ArrowUp : ArrowDown;
    return <Icon size={11} className="text-[#1F1A17]" />;
  };

  const ariaSort = (sorts) => {
    if (!sorts?.includes(sort)) return 'none';
    return ASC_SORTS.includes(sort) ? 'ascending' : 'descending';
  };

  const sortableTh = (col) => (
    <th key={col.id} className={`${thBase} bg-[#FFF4E8] border-b border-border`} aria-sort={col.sorts ? ariaSort(col.sorts) : undefined}>
      {col.sorts ? (
        <button
          onClick={() => clickSort(col.sorts)}
          className="inline-flex items-center gap-1 hover:text-[#1F1A17] transition-colors uppercase tracking-wide"
        >
          {col.label}
          {sortCaret(col.sorts)}
        </button>
      ) : col.label}
    </th>
  );

  // Parent tabs (origin) — editors/admin only
  const parentTabs = isAdminOrEditor && (
    <div className="flex gap-1 bg-[#FFF4E8] p-1 rounded-xl border border-border w-fit">
      {PARENT_TABS.map(t => (
        <button
          key={t.id}
          onClick={() => changeParent(t.id)}
          data-testid={`origin-tab-${t.id}`}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
            origin === t.id
              ? 'bg-[#FFE4C4] text-[#1F1A17] border border-[#E7D2B8] shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-[#FFE4C4]/50'
          }`}
        >
          {t.label}
          {counts?.[t.id] && (
            <span className="text-[11px] text-[#9C8E84]">{counts[t.id].all ?? 0}</span>
          )}
        </button>
      ))}
    </div>
  );

  // Sub-tabs (status)
  const tabStrip = (
    <div className="flex gap-1 flex-wrap border-b border-border">
      {subtabs.map(sub => {
        const n = countFor(countScope, sub);
        const active = status === sub;
        return (
          <button
            key={sub}
            onClick={() => changeStatus(sub)}
            data-testid={`status-tab-${sub}`}
            className={`flex items-center gap-1 px-2.5 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              active
                ? 'border-[#1F1A17] text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-[#E7D2B8]'
            }`}
          >
            {SUBTAB_LABELS[sub]}
            {n != null && (
              <span className={`text-[11px] tabular-nums leading-none px-1 py-0.5 rounded ${
                n === 0
                  ? 'text-[#C9B49A]'
                  : active ? 'bg-[#FFE4C4] text-[#1F1A17]' : 'text-[#9C8E84]'
              }`}>{n}</span>
            )}
          </button>
        );
      })}
    </div>
  );

  if (status === 'suppressed') {
    return (
      <div className="space-y-5 animate-fade-in pb-16" data-testid="stories-page">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-foreground font-outfit tracking-tight">Newsroom</h1>
        </div>
        {parentTabs}
        {tabStrip}
        <SuppressedTab
          apiGet={async (params) => (await api.get('/stories/suppressed', { params })).data}
          apiUnsuppress={(id) => api.post(`/stories/suppressed/${id}/unsuppress`, {})}
          canUnsuppress={user?.role === 'admin'}
        />
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-fade-in pb-16" data-testid="stories-page">

      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground font-outfit tracking-tight">Newsroom</h1>
        <Button
          data-testid="create-story-btn"
          onClick={() => navigate('/stories/new')}
          className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]"
        >
          New Story
        </Button>
      </div>

      {parentTabs}

      {tabStrip}

      {status === 'deleted' && (
        <p className="text-xs text-[#9C8E84] -mt-2">
          Deleted stories are kept for 7 days, then permanently removed. Restore anything you need before then.
        </p>
      )}

      {status === 'scheduled' && (
        <div className="flex items-center justify-between gap-4 flex-wrap -mt-2">
          <p className="text-xs text-[#9C8E84]">
            Scheduled stories stay off the live site until their publication time, then go live automatically.
          </p>
          <LondonClock />
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 min-w-[240px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9C8E84]" size={18} />
          <Input
            ref={searchInput}
            data-testid="search-stories-input"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search headline, intro, body, source or subject…"
            className="pl-10 pr-9 h-10 rounded-lg bg-white text-[#1F1A17] border-border placeholder:text-[#9C8E84] focus-visible:ring-1 focus-visible:ring-[#C9B49A] focus-visible:border-[#C9B49A]"
          />
          {search ? (
            <button
              type="button"
              onClick={clearSearch}
              title="Clear search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9C8E84] hover:text-foreground transition-colors"
            >
              <X size={15} />
            </button>
          ) : (
            <kbd className="hidden sm:flex absolute right-2.5 top-1/2 -translate-y-1/2 h-5 w-5 items-center justify-center rounded border border-border bg-[#FFF4E8] text-[11px] text-[#9C8E84] pointer-events-none">/</kbd>
          )}
        </form>

        <Select value={sort} onValueChange={changeSort}>
          <SelectTrigger className="w-40 h-10 bg-card border-border text-foreground" data-testid="sort-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            {SORT_OPTIONS.map(o => (
              <SelectItem key={o.value} value={o.value} className="text-foreground focus:bg-[#FFF4E8]">{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowFilters(!showFilters)}
          className={`border-border text-foreground hover:bg-[#FFF4E8] gap-1.5 h-10 ${showFilters ? 'bg-[#FFE4C4] border-[#C9B49A]' : ''}`}
        >
          <Filter size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span className="bg-amber-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </Button>

        <ColumnsMenu
          hidden={hiddenCols}
          density={density}
          onDensity={setDensity}
          onToggle={id => setHiddenCols(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
          })}
          onReset={() => setHiddenCols(new Set())}
        />
      </div>

      {chips.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap -mt-2">
          {chips.map(c => (
            <span key={c.key} className="inline-flex items-center gap-1 rounded-full bg-[#FFF4E8] border border-border pl-2.5 pr-1 py-1 text-xs text-[#6E6258]">
              {c.label}
              <button onClick={() => removeFilter(c.key)} title="Remove filter" className="h-4 w-4 inline-flex items-center justify-center rounded-full hover:bg-[#FFE4C4] hover:text-foreground transition-colors">
                <X size={11} />
              </button>
            </span>
          ))}
          <button onClick={clearFilters} className="text-xs text-[#9C8E84] hover:text-foreground ml-1 underline underline-offset-2">Clear all</button>
        </div>
      )}

      {showFilters && (
        <Card className="border-border animate-in fade-in-0 slide-in-from-top-1 duration-200">
          <CardContent className="p-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Client</label>
                <Select value={filters.client_id} onValueChange={v => { setFilter('client_id', v); setFilter('project_name', ''); }}>
                  <SelectTrigger className="bg-white text-[#1F1A17] border-border h-9 text-sm"><SelectValue placeholder="All Clients" /></SelectTrigger>
                  <SelectContent className="bg-white border-border">
                    <SelectItem value="all" className="text-[#1F1A17] focus:bg-[#FFF4E8]">All Clients</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[#1F1A17] focus:bg-[#FFF4E8]">{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Project</label>
                <Select value={filters.project_name || 'all'} onValueChange={v => setFilter('project_name', v === 'all' ? '' : v)} disabled={filters.client_id === 'all'}>
                  <SelectTrigger className="bg-white text-[#1F1A17] border-border h-9 text-sm"><SelectValue placeholder="All Projects" /></SelectTrigger>
                  <SelectContent className="bg-white border-border">
                    <SelectItem value="all" className="text-[#1F1A17] focus:bg-[#FFF4E8]">All Projects</SelectItem>
                    {clientProjects.map(p => <SelectItem key={p} value={p} className="text-[#1F1A17] focus:bg-[#FFF4E8]">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Publication</label>
                <Select value={filters.publication_id} onValueChange={v => setFilter('publication_id', v)}>
                  <SelectTrigger className="bg-white text-[#1F1A17] border-border h-9 text-sm"><SelectValue placeholder="All Publications" /></SelectTrigger>
                  <SelectContent className="bg-white border-border">
                    <SelectItem value="all" className="text-[#1F1A17] focus:bg-[#FFF4E8]">All Publications</SelectItem>
                    {publications.map(p => <SelectItem key={p.id} value={p.id} className="text-[#1F1A17] focus:bg-[#FFF4E8]">{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Story Type</label>
                <Select value={filters.story_type} onValueChange={v => setFilter('story_type', v)}>
                  <SelectTrigger className="bg-white text-[#1F1A17] border-border h-9 text-sm"><SelectValue placeholder="All Types" /></SelectTrigger>
                  <SelectContent className="bg-white border-border">
                    <SelectItem value="all" className="text-[#1F1A17] focus:bg-[#FFF4E8]">All Types</SelectItem>
                    {storyTypes.length > 0
                      ? storyTypes.map(st => <SelectItem key={st.value} value={st.value} className="text-[#1F1A17] focus:bg-[#FFF4E8]">{st.label}</SelectItem>)
                      : Object.entries(storyTypeLabels).map(([v, l]) => <SelectItem key={v} value={v} className="text-[#1F1A17] focus:bg-[#FFF4E8]">{l}</SelectItem>)
                    }
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Tag</label>
                <Select value={filters.tag} onValueChange={v => setFilter('tag', v)}>
                  <SelectTrigger className="bg-white text-[#1F1A17] border-border h-9 text-sm"><SelectValue placeholder="All Tags" /></SelectTrigger>
                  <SelectContent className="bg-white border-border">
                    <SelectItem value="all" className="text-[#1F1A17] focus:bg-[#FFF4E8]">All Tags</SelectItem>
                    {uniqueTags.map(t => <SelectItem key={t} value={t} className="text-[#1F1A17] focus:bg-[#FFF4E8]">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {isAdminOrEditor && (
                <div>
                  <label className="text-xs text-[#6E6258] mb-1 block">Caster / Editor</label>
                  <Select value={filters.caster_id} onValueChange={v => setFilter('caster_id', v)}>
                    <SelectTrigger className="bg-white text-[#1F1A17] border-border h-9 text-sm"><SelectValue placeholder="All Users" /></SelectTrigger>
                    <SelectContent className="bg-white border-border">
                      <SelectItem value="all" className="text-[#1F1A17] focus:bg-[#FFF4E8]">All Users</SelectItem>
                      {casters.map(c => <SelectItem key={c.id} value={c.id} className="text-[#1F1A17] focus:bg-[#FFF4E8]">{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Geography</label>
                <input type="text" value={filters.geography} onChange={e => setFilter('geography', e.target.value)} placeholder="e.g. Europe, Global…" className="w-full bg-white text-[#1F1A17] border border-border rounded-md h-9 px-3 text-sm placeholder-[#9C8E84] focus:outline-none focus:border-[#1F1A17]" />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Created from</label>
                <input type="date" value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} className="w-full bg-white text-[#1F1A17] border border-border rounded-md h-9 px-3 text-sm focus:outline-none focus:border-[#1F1A17]" />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Created to</label>
                <input type="date" value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} className="w-full bg-white text-[#1F1A17] border border-border rounded-md h-9 px-3 text-sm focus:outline-none focus:border-[#1F1A17]" />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Published from</label>
                <input type="date" value={filters.date_published_from} onChange={e => setFilter('date_published_from', e.target.value)} className="w-full bg-white text-[#1F1A17] border border-border rounded-md h-9 px-3 text-sm focus:outline-none focus:border-[#1F1A17]" />
              </div>
              <div>
                <label className="text-xs text-[#6E6258] mb-1 block">Published to</label>
                <input type="date" value={filters.date_published_to} onChange={e => setFilter('date_published_to', e.target.value)} className="w-full bg-white text-[#1F1A17] border border-border rounded-md h-9 px-3 text-sm focus:outline-none focus:border-[#1F1A17]" />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              {activeFilterCount > 0 && (
                <Button variant="ghost" onClick={clearFilters} className="text-muted-foreground hover:text-foreground h-9 px-4 text-sm">Clear all</Button>
              )}
              <Button onClick={applyFilters} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4] h-9 px-6 text-sm">Apply filters</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between min-h-[24px]">
        <p className="text-xs text-[#9C8E84]">
          {firstLoad
            ? 'Loading stories…'
            : total === 0
              ? 'No stories'
              : `${total} stor${total === 1 ? 'y' : 'ies'}${total > PAGE_SIZE ? ` — page ${page} of ${totalPages}` : ''}`
          }
        </p>
        {selected.size > 0 && (
          <p className="text-xs text-[#6E6258] font-medium">{selected.size} selected</p>
        )}
      </div>

      {firstLoad ? (
        <div className="rounded-xl border border-border bg-white shadow-sm overflow-hidden">
          <div className="h-11 bg-[#FFF4E8] border-b border-border" />
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 px-3 border-b border-border last:border-0" style={{ height: compact ? 34 : 49 }}>
              <div className="h-4 w-4 rounded bg-[#F8EBD9] animate-pulse shrink-0" />
              <div className="h-3 rounded bg-[#F8EBD9] animate-pulse" style={{ width: `${34 - (i % 4) * 5}%` }} />
              <div className="h-3 w-20 rounded bg-[#F8EBD9] animate-pulse" />
              <div className="h-3 w-16 rounded bg-[#F8EBD9] animate-pulse" />
              <div className="h-3 w-24 rounded bg-[#F8EBD9] animate-pulse ml-auto" />
            </div>
          ))}
        </div>
      ) : stories.length === 0 ? (
        <Card className="border-border">
          <CardContent className="py-16 text-center">
            <FileText className="mx-auto text-[#E7D2B8] mb-3" size={44} />
            <p className="text-foreground text-lg font-medium">{emptyCopy().title}</p>
            <p className="text-muted-foreground text-sm mt-1">{emptyCopy().hint}</p>
            {(searchRef.current || activeFilterCount > 0) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { clearSearch(); clearFilters(); }}
                className="mt-4 border-border text-foreground hover:bg-[#FFF4E8]"
              >
                Clear search and filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="relative">
            {refetching && (
              <div className="absolute inset-0 z-40 bg-white/50 backdrop-blur-[1px] rounded-xl flex items-start justify-center pt-8 pointer-events-none">
                <span className="inline-flex items-center gap-2 rounded-full bg-white border border-border shadow-sm px-3 py-1.5 text-xs text-[#6E6258]">
                  <Loader2 size={13} className="animate-spin" /> Updating…
                </span>
              </div>
            )}

            <div className="rounded-xl border border-border bg-white shadow-sm overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-[#FFF4E8]">
                    <th className="sticky left-0 z-30 bg-[#FFF4E8] w-12 min-w-12 px-3 py-3 border-b border-border rounded-tl-xl">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 rounded border-[#C9B49A] accent-[#1F1A17] cursor-pointer align-middle"
                        title="Select all on this page"
                        aria-label="Select all stories on this page"
                      />
                    </th>
                    <th
                      className={`${thBase} sticky left-12 z-30 bg-[#FFF4E8] border-r border-b border-border min-w-[240px]`}
                      aria-sort={ariaSort(['headline'])}
                    >
                      <button onClick={() => clickSort(['headline'])} className="inline-flex items-center gap-1 hover:text-[#1F1A17] transition-colors uppercase tracking-wide">
                        Headline
                        {sortCaret(['headline'])}
                      </button>
                    </th>
                    {COLUMNS.filter(c => showCol(c.id)).map(c => sortableTh(c))}
                    <th className={`${thBase} sticky right-0 z-30 bg-[#FFF4E8] border-l border-b border-border text-right rounded-tr-xl`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {stories.map((story, idx) => {
                    const isSelected = selected.has(story.id);
                    const bothNames  = story.approved_by_name && ['published', 'unpublished'].includes(story.status);
                    const stickyBg   = isSelected ? 'bg-[#FFF7EC]' : 'bg-white group-hover:bg-[#FFF4E8]';
                    return (
                      <tr
                        key={story.id}
                        tabIndex={0}
                        className={`group border-b border-border last:border-0 cursor-pointer transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#C9B49A] ${isSelected ? 'bg-[#FFF7EC]' : 'hover:bg-[#FFF4E8]'}`}
                        onClick={() => navigate(`/stories/${story.id}`)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') navigate(`/stories/${story.id}`);
                          if (e.key === 'x' || e.key === ' ') { e.preventDefault(); toggleSelect(story.id, idx, e.shiftKey); }
                        }}
                        data-testid={`story-row-${story.id}`}
                      >
                        <td className={`sticky left-0 z-10 w-12 min-w-12 px-3 ${cellY} ${stickyBg}`} onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {}}
                            onClick={e => { e.stopPropagation(); toggleSelect(story.id, idx, e.shiftKey); }}
                            className="h-4 w-4 rounded border-[#C9B49A] accent-[#1F1A17] cursor-pointer align-middle"
                            aria-label={`Select ${story.headline || 'Untitled'}`}
                          />
                        </td>
                        <td className={`${tdBase} sticky left-12 z-10 border-r border-border min-w-[240px] max-w-[360px] ${stickyBg}`}>
                          <div className="flex items-center gap-1.5">
                            {showOriginIcon && <OriginIcon origin={story.story_origin} />}
                            {story.editorial_locked && <Lock size={12} className="text-amber-600 shrink-0" title="Locked from Okebourne" />}
                            {story.last_publish_error && <AlertTriangle size={12} className="text-red-500 shrink-0" title={story.last_publish_error} />}
                            <span className="block truncate font-medium text-foreground group-hover:text-blue-600 transition-colors" title={story.headline || 'Untitled'}>
                              {story.headline || 'Untitled'}
                            </span>
                          </div>
                        </td>
                        {showCol('publication') && <td className={`${tdBase} text-[#6E6258] max-w-[200px] truncate`}>{story.publication_name || '—'}</td>}
                        {showCol('status') && <td className={tdBase}><StatusBadge status={story.status} /></td>}
                        {showCol('assigned') && (
                          <td className={`${tdBase} max-w-[180px]`}>
                            <div className="truncate">
                              <span className="text-[#6E6258]">{story.created_by_name || '—'}</span>
                              {bothNames && !compact && <span className="block text-xs text-[#9C8E84] truncate">✓ {story.approved_by_name}</span>}
                            </div>
                          </td>
                        )}
                        {showCol('client') && (
                          <td className={`${tdBase} max-w-[220px]`}>
                            {(story.client_name || story.project_name) ? (
                              <div className="truncate">
                                <span className="text-foreground">{story.client_name || '—'}</span>
                                {story.project_name && !compact && <span className="block text-xs text-[#9C8E84] truncate">{story.project_name}</span>}
                              </div>
                            ) : <span className="text-[#9C8E84]">—</span>}
                          </td>
                        )}
                        {showCol('subject') && <td className={`${tdBase} text-[#6E6258] max-w-[180px] truncate`}>{story.organisation_name || story.person_name || '—'}</td>}
                        {showCol('type') && <td className={`${tdBase} text-[#6E6258]`}>{storyTypeLabels[story.story_type] || story.story_type || '—'}</td>}
                        {showCol('tags') && (
                          <td className={tdBase}>
                            {story.tags?.length ? (
                              <div className="flex items-center gap-1">
                                {story.tags.slice(0, 2).map(t => (
                                  <span key={t} className="px-1.5 py-0.5 rounded bg-[#F8EBD9] text-[#6E6258] text-[11px] whitespace-nowrap">{t}</span>
                                ))}
                                {story.tags.length > 2 && <span className="text-[11px] text-[#9C8E84]" title={story.tags.join(', ')}>+{story.tags.length - 2}</span>}
                              </div>
                            ) : <span className="text-[#9C8E84]">—</span>}
                          </td>
                        )}
                        {showCol('scheduled') && (
                          <td className={`${tdBase} text-[#6E6258]`}>
                            {story.scheduled_for ? (
                              <div>
                                <span className={story.last_publish_error ? 'text-red-600' : ''}>{fmtUK(story.scheduled_for)}</span>
                                {!compact && <span className="block text-xs text-[#9C8E84]">{relativeTo(story.scheduled_for)}</span>}
                              </div>
                            ) : <span className="text-[#9C8E84]">—</span>}
                          </td>
                        )}
                        {showCol('public_date') && <td className={`${tdBase} text-[#6E6258]`}>{fmtUK(story.public_display_at || story.published_at)}</td>}
                        {showCol('created') && <td className={`${tdBase} text-[#6E6258]`}>{fmtUK(story.created_at)}</td>}
                        {showCol('updated') && (
                          <td className={`${tdBase} text-[#6E6258]`}>
                            <div>
                              <span>{fmtUK(story.updated_at)}</span>
                              {story.updated_by_name && !compact && <span className="block text-xs text-[#9C8E84] truncate">by {story.updated_by_name}</span>}
                            </div>
                          </td>
                        )}
                        <td className={`${tdBase} sticky right-0 z-10 border-l border-border ${stickyBg}`}>
                          {renderActions(story)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-4 pt-4 mt-2 border-t border-border flex-wrap">
              <p className="text-xs text-[#9C8E84] order-2 sm:order-1">
                Showing <span className="font-medium text-[#6E6258]">{rangeStart}–{rangeEnd}</span> of {total}
              </p>

              <div className="flex items-center gap-1 order-1 sm:order-2 ml-auto sm:ml-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                  className="border-border text-foreground hover:bg-[#FFF4E8] gap-1 h-8 px-3 disabled:opacity-40"
                >
                  <ChevronLeft size={16} /> Prev
                </Button>

                <div className="flex items-center gap-1 mx-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-1.5 text-[#9C8E84] text-sm select-none">…</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => handlePageChange(p)}
                          className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                            p === page
                              ? 'bg-[#1F1A17] text-[#FFE4C4]'
                              : 'text-foreground hover:bg-[#FFF4E8] border border-transparent hover:border-border'
                          }`}
                        >
                          {p}
                        </button>
                      )
                    )
                  }
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="border-border text-foreground hover:bg-[#FFF4E8] gap-1 h-8 px-3 disabled:opacity-40"
                >
                  Next <ChevronRight size={16} />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in fade-in-0 slide-in-from-bottom-2 duration-200">
          <div className="flex items-center gap-2 flex-wrap rounded-xl border border-[#E7D2B8] bg-[#1F1A17] px-3 py-2 shadow-xl">
            <span className="text-xs font-medium text-[#FFE4C4] px-1">
              {selected.size} selected
            </span>
            <span className="w-px h-5 bg-white/15" />
            {bulkFor().map(b => (
              <Button
                key={b.action}
                size="sm"
                variant="ghost"
                disabled={bulkBusy}
                onClick={() => askBulk(b.action, b.label)}
                className={`h-7 text-xs gap-1 hover:bg-white/10 ${b.danger ? 'text-red-300 hover:text-red-200' : 'text-[#FFE4C4]'}`}
              >
                <b.icon size={13} /> {b.label}
              </Button>
            ))}
            {isAdminOrEditor && status !== 'deleted' && schedulablePicked.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                disabled={bulkBusy}
                onClick={() => setShowSchedule(true)}
                className="h-7 text-xs gap-1 text-[#FFE4C4] hover:bg-white/10"
              >
                <Clock size={13} /> {status === 'scheduled' ? 'Reschedule' : 'Schedule'}
              </Button>
            )}
            {isAdminOrEditor && status !== 'deleted' && (
              <Button
                size="sm"
                variant="ghost"
                disabled={bulkBusy}
                onClick={() => setShowBackdate(true)}
                className="h-7 text-xs gap-1 text-[#FFE4C4] hover:bg-white/10"
              >
                <CalendarClock size={13} /> Set public date
              </Button>
            )}
            {bulkBusy && <Loader2 size={14} className="animate-spin text-[#FFE4C4]" />}
            <span className="w-px h-5 bg-white/15" />
            <button
              onClick={() => setSelected(new Set())}
              title="Clear selection (Esc)"
              className="h-6 w-6 inline-flex items-center justify-center rounded-md text-[#C9B49A] hover:text-[#FFE4C4] hover:bg-white/10 transition-colors"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      <Dialog open={!!confirm} onOpenChange={o => !o && setConfirm(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">{confirm?.title}</DialogTitle>
            <DialogDescription className="text-muted-foreground">{confirm?.body}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)} className="text-muted-foreground hover:text-foreground">Cancel</Button>
            <Button variant="destructive" onClick={() => confirm?.onConfirm?.()} disabled={bulkBusy || !!busyId}>
              {(bulkBusy || !!busyId) && <Loader2 size={14} className="animate-spin mr-1.5" />}
              {confirm?.confirmLabel || 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showSchedule && (
        <BulkScheduleModal
          stories={schedulablePicked}
          skipped={skippedPicked}
          onClose={() => setShowSchedule(false)}
          onApplied={(ok, failures) => {
            setShowSchedule(false);
            setSelected(new Set());
            refresh();
            if (failures.length) setBulkResults({ ok, failures });
            else toast.success(`${ok} ${ok === 1 ? 'story' : 'stories'} scheduled`);
          }}
        />
      )}

      {showBackdate && (
        <BackdateModal
          stories={stories.filter(s => selected.has(s.id))}
          onClose={() => setShowBackdate(false)}
          onApplied={() => { setShowBackdate(false); setSelected(new Set()); refresh(); }}
        />
      )}

      <Dialog open={!!bulkResults} onOpenChange={o => !o && setBulkResults(null)}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-600" />
              {bulkResults?.ok || 0} done, {bulkResults?.failures?.length || 0} couldn't be
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              These stories are missing required fields and were left as they were:
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 overflow-auto space-y-2">
            {bulkResults?.failures?.map((f, i) => (
              <div key={i} className="rounded-md border border-border px-3 py-2">
                <p className="text-sm font-medium text-foreground truncate">{f.headline}</p>
                <p className="text-xs text-red-600">{f.detail}</p>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setBulkResults(null)} className="bg-[#1F1A17] hover:bg-[#2B241F] text-[#FFE4C4]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}